<template>
  <div>
    <ValidationObserver
      ref="fileObserver" >
      <ValidationProvider
        v-slot="{ errors }"
        :name="schema.title"
        :rules="validators">
        <b-button
          :disabled="scrapingInProgress || !!scrapeDeleteData || !!scrapingData"
          class="url-button flex flex-align-center icon"
          :style="{ 'background-image': scrapingInProgress || !!scrapeDeleteData || !!scrapingData ? 'unset' : schema.addButtonIcon ? `url(${schema.addButtonIcon})` : '' }"
          @click="showUrlsModal">
          <span
            v-if="scrapingInProgress || !!scrapeDeleteData || !!scrapingData"
            style="margin-left: -22px;"
            class="flex flex-align-center" >
            <CircleSpinner
              :diameter="14"
              infinite
              :stroke-width="2"
              foreground="#141c20"
              background="#CFD8DC" />
            <p class="action-text dark ml-2">Processing links...</p>
          </span>
          <span
            v-if="!scrapingInProgress && !scrapeDeleteData && !scrapingData && !buttonUrl && !linkCount"
            class="subtle" >{{ schema.addButtonText }}</span>
          <template v-else-if="!scrapingInProgress && !scrapeDeleteData && !scrapingData">
            <span
              v-if="buttonUrl"
              class="ellipsis mr-1" >{{ buttonUrl }}</span>
            <span
              v-if="linkCount"
              class="mr-1 no-shrink">{{ buttonUrl ? ' +' : '' }} {{ linkCount }} link{{ linkCount > 1 ? 's' : '' }}</span>
            <span class="greedy" />
            <div
              v-if="!scrapingInProgress && !scrapeDeleteData && !scrapingData && filterCounter"
              class="filter-btn ml-2 flex center-all" >
              <SBIcon
                class="mal-1"
                :icon="SBIconList.FILTER"
                :color="SBColors.blackUltralight"
                :size="10" />
              <span class="p-0 m-0">{{ filterCounter }}</span>
            </div>
            <!-- Failed flag -->
            <SBIcon
              v-if="flags && flags.fail"
              class="ml-2"
              style="margin-right: -4px;"
              :icon="SBIconList.TRIANGLE_EXCLAMATION"
              :color="SBColors.redPrimary"
              :size="12" />
          </template>
        </b-button>
        <!-- SUBTITLE -->
        <div
          v-if="scrapingInProgress || scrapeDeleteData || scrapingData"
          class="subtitle flex flex-align-center show-hand"
          @click="cancelScrape" >
          <SBIcon
            class="mr-1"
            :icon="SBIconList.CLOSE_THIN"
            :color="SBColors.redPrimary"
            :size="10" />
          <span class="subtle error non-blocking">Cancel</span>
        </div>
        <div
          v-else-if="!hideScrape && previousScrape && !errors[0]"
          class="subtitle flex flex-align-center" >
          <div
            v-if="!hasChanges || forceRescrapeButton"
            class="flex flex-align-center mr-1 show-hand rescrape-btn"
            @click="reScrape">
            <SBIcon
              class="mr-1"
              :icon="SBIconList.RECYCLE"
              :color="SBColors.bluePrimary"
              :size="10" />
            <span
              class="blue">Rescrape</span>
          </div>
          <span class="subtle ellipsis">(last scrape: {{ previousScrape | dateRegionDMYYY }} at {{ previousScrape | subMessageDate }})</span>
        </div>
        <div
          v-else-if="schema.subtitle && !errors[0]"
          class="subtitle flex flex-align-center">
          <svg
            v-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string' && schema.subtitlePrefixIcon.endsWith('.svg')"
            class="mr-1 flex-start-image"
            :data-src="schema.subtitlePrefixIcon"
            fill="currentColor"
            width="10"
            height="10" />
          <img
            v-else-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string'"
            :src="schema.subtitlePrefixIcon"
            style="width: 10px; height: 10px;"
            class="mr-1 flex-start-image"
          >
          <SBIcon
            v-else-if="!schema.noSubtitlePrefixIcon"
            class="mr-1"
            :icon="SBIconList.INFO"
            :color="SBColors.greyLight"
            :size="10" />
          <span v-html="schema.subtitle" />
        </div>
      </ValidationProvider>
    </ValidationObserver>

    <UrlInputModal
      ref="urlInputModalRef"
      :key="`${randomKey}-${resetModalKey}`"
      :elements="(updatedSchema && updatedSchema.children) ? updatedSchema.children : schema.children"
      :domain-scrape-finished="(updatedSchema && updatedSchema.domainLinksScrapeFinished) ? updatedSchema.domainLinksScrapeFinished : schema.domainLinksScrapeFinished"
      :modal-title="schema.modalTitle"
      :modal-subtitle="schema.modalSubtitle"
      @input="setData"
      @closed="modalClosed"
      @reset="resetModal" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import CircleSpinner from '@/components/animatedIcons/CircleSpinner';
import api from '@/helpers/api';
import randomString from '@/helpers/randomString';
import { ModalSize, ModalVariant } from '@/helpers/enums/modal.enum';
import UrlInputModal from './UrlInput.modal';
import StatusTypes from '../../../helpers/enums/statusTypes.enum';

export default {
  name: 'UrlInputWrapper',
  components: {
    UrlInputModal,
    CircleSpinner,
    SBIcon,
  },
  props: {
    validators: {
      type: String,
      default: '',
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    propName: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      initialized: false,
      urls: {},
      urlsBackup: {},
      dirty: false,
      hasChanges: false,
      updatedModalSchema: null,
      updateModalKey: 0,
      preventEmit: false,
      childrenBody: {},
      resetModalKey: 0,
      SBColors,
      SBIconList,
      updatedSchema: null,
      forceRescrapeButton: false,
      flags: null,
      specificCounterOverride: null,
      urlOverride: null,
      syncElementSchema: null,
      randomKey: null,
    };
  },
  computed: {
    ...mapGetters({
      backgroundActions: 'automation/backgroundActions',
      currentOrganisation: 'organisation/currentOrganisation',
      updatedAISchema: 'automation/updatedAISchema',
      scrapingStatus: 'automation/scrapingStatus',
      // updatedCounter: 'automation/updatedCounter',
      webScrapeCounter: 'automation/webScrapeCounter',
      webScrapeDeleteCounter: 'automation/webScrapeDeleteCounter',
    }),
    assistantId() {
      return this.$route.params.assistantId;
    },
    scrapingInProgress() {
      return this.scrapingStatus[this.assistantId];
    },
    urlCount() {
      if (!this.urls.url || !this.urls.url.data || !this.urls.url.data.value) {
        return 0;
      }
      return this.urls.url.data.value.split(',').length;
    },
    buttonUrl() {
      if (this.urlOverride && this.urlOverride !== '') {
        return this.$options.filters.domainSimplified(this.urlOverride);
      }
      if (this.urlOverride !== '' && this.urls && this.urls.entireWeb && this.urls.entireWeb.value) {
        return this.$options.filters.domainSimplified(this.urls.entireWeb.value);
      }
      return null;
    },
    previousScrape() {
      if (this.updatedSchema) {
        return Math.max(this.updatedSchema.domainLinksScrapeFinished, this.updatedSchema.specificLinksScrapeFinished);
      }
      return Math.max(this.schema.domainLinksScrapeFinished, this.schema.specificLinksScrapeFinished);
    },
    hideScrape() {
      return (this.urls.entireWeb && this.urls.entireWeb.delete) && !this.linkCount;
    },
    linkCount() {
      const countHere = this.updatedSchema && this.updatedSchema.children ? this.updatedSchema.children : (this.schema && this.schema.children ? this.schema.children : null);
      const countNewSpecific = this.urls && this.urls.specificLinks && this.urls.specificLinks.value ? this.urls.specificLinks.value.filter(v => v).length : 0;
      const countRemovedSpecific = this.urls && this.urls.removedSpecificLinks && this.urls.removedSpecificLinks.value ? this.urls.removedSpecificLinks.value.length : 0;
      let currentCount = 0;
      if (this.specificCounterOverride !== null) {
        currentCount = this.specificCounterOverride;
      } else if (countHere) {
        currentCount = countHere && countHere.specificScrapedLinksPagination && countHere.specificScrapedLinksPagination.count ? countHere.specificScrapedLinksPagination.count : 0;
      }

      return currentCount + countNewSpecific - countRemovedSpecific;
    },
    filterCounter() {
      // const targetKey = Object.keys(this.urls).find(k => this.schema.populatedText.includes(k));
      // const targetElement = this.urls[targetKey];
      // if (targetElement && targetElement.data && Array.isArray(targetElement.data)) {
      //   return targetElement.data.length;
      // }
      // return 0;

      let filterCounter = 0;
      if (this.urls && this.urls.excludePaths && this.urls.excludePaths.value) {
        filterCounter += this.urls.excludePaths.value.length;
      }
      if (this.urls && this.urls.includePaths && this.urls.includePaths.value) {
        filterCounter += this.urls.includePaths.value.length;
      }

      return filterCounter;
    },
    scrapingData() {
      return this.webScrapeCounter[this.assistantId];
    },
    scrapeDeleteData() {
      return this.webScrapeDeleteCounter[this.assistantId];
    },
  },
  watch: {
    backgroundActions() {
      const targetScrape = this.backgroundActions.filter(ba => ba.assistantId === this.assistantId && ba.action === 'webScrape' && [StatusTypes.COMPLETED].includes(ba.status));
      if (this.updatedSchema && targetScrape && targetScrape[0] && targetScrape[0].status === StatusTypes.COMPLETED && targetScrape[0].finished) {
        switch (targetScrape[0].type) {
          case 'specific':
            this.updatedSchema.specificLinksScrapeFinished = Math.floor(targetScrape[0].timestamp / 1000);
            break;
          default:
            this.updatedSchema.domainLinksScrapeFinished = Math.floor(targetScrape[0].timestamp / 1000);
            break;
        }

        this.resetModalKey++;
      }
    },
    urls: {
      handler() {
        this.emitData();
      },
      immediate: true,
    },
    /**
     * updatedAISchema is updated every time form submits and the new schema is saved so each element has to check if the new data is available for it and needs to update.
     */
    updatedAISchema: {
      handler() {
        const targetValue = this.getObjectCopyByKey(this.updatedAISchema, this.propName);
        if (targetValue) {
          // this.updatedModalSchema = JSON.parse(JSON.stringify(targetValue.modal));
          if (targetValue.children) {
            this.updatedSchema = targetValue;
            this.resetModalKey++;
          }
          this.preventEmit = true;
          this.urls = {};
          this.urlsBackup = {};
          this.$nextTick(() => {
            this.updateModalKey++;
            this.$nextTick(() => {
              this.urlsBackup = JSON.parse(JSON.stringify(this.urls));
              this.preventEmit = false;
              this.emitData(true, true);
            });
          });
        }
      },
      immediate: true,
    },
    /**
     * updatedCounter is updated every time the counter is updated via pusher so the component can show links if needed.
     */
    // updatedCounter() {
      // const type = this.updatedCounter.url === 'specific links' ? 'specific' : 'all';

      // if (!this.updatedSchema || !this.updatedSchema.children) return;

      // switch (type) {
      //   case 'all':
      //     this.updatedSchema.children.domainCrawledLinksPagination.count = this.updatedCounter.links_count;
      //     this.updatedSchema.domainLinksScrapeFinished = Math.floor(Date.now() / 1000);
      //     break;
      //   case 'specific':
      //     this.updatedSchema.children.specificScrapedLinksPagination.count = this.updatedCounter.links_count;
      //     this.updatedSchema.specificLinksScrapeFinished = Math.floor(Date.now() / 1000);
      //     break;
      //   default:
      //     break;
      // }
      // this.resetModalKey++;
    // },
    scrapingInProgress: {
      handler() {
        this.setScrapingStatus({
          assistantId: this.assistantId,
          status: this.scrapingInProgress,
        });
        if (!this.scrapingInProgress) {
          this.resetModal();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.randomKey = randomString(10);
    if (this.schema.value) {
      // Set files on edit
    }
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (!this.$refs.fileObserver) return;
        this.$refs.fileObserver.validate();
      });
    }
    // SET FLAGS
    try {
      if (this.schema.flags.delete.domain_links) {
        this.updateDeleteCounter({ url: 'all', ai_assistant_uid: this.assistantId });
      }
      if (this.schema.flags.scrape.domain_links) {
        this.addBackgroundActions([{
          action: 'webScrape',
          type: 'all',
          assistantId: this.assistantId,
          status: StatusTypes.RUNNING,
        }]);
      }
      if (this.schema.flags.delete.specific_links) {
        this.updateDeleteCounter({ url: 'specific', ai_assistant_uid: this.assistantId });
      }
      if (this.schema.flags.scrape.specific_links) {
        this.addBackgroundActions([{
          action: 'webScrape',
          type: 'specific',
          assistantId: this.assistantId,
          status: StatusTypes.RUNNING,
        }]);
      }
    } catch (error) {}

    this.flags = this.schema.flags;

    if (this.schema.domainLinksScrapeFinished === false) {
      this.addBackgroundActions([{
        type: 'all',
        action: 'webScrape',
        assistantId: this.assistantId,
        status: StatusTypes.RUNNING,
      }]);
    }
    if (this.schema.specificLinksScrapeFinished === false) {
      this.addBackgroundActions([{
        type: 'specific',
        action: 'webScrape',
        assistantId: this.assistantId,
        status: StatusTypes.RUNNING,
      }]);
    }

    window.eventBus.$on('ai-url-update', this.aiUIupdate);

    setTimeout(() => {
      this.emitData(true, true);
      if (this.schema.value) {
        if (!this.$refs.fileObserver) return;
        this.$refs.fileObserver.validate();
      }
      this.urlsBackup = JSON.parse(JSON.stringify(this.urls));
      this.hasChanges = false;
    }, 0);
  },
  destroyed() {
    window.eventBus.$off('ai-url-update', this.aiUIupdate);
  },
  methods: {
    ...mapActions({
      addBackgroundActions: 'automation/addBackgroundActions',
      backgroundActionStatusChange: 'automation/backgroundActionStatusChange',
      setScrapingStatus: 'automation/setScrapingStatus',
      updateDeleteCounter: 'automation/updateDeleteCounter',
    }),
    aiUIupdate(data) {
      if (data.ai_assistant_uid === this.assistantId) {
        if (!this.updatedSchema) {
          this.updatedSchema = JSON.parse(JSON.stringify(this.schema));
        }

        if (data.action === 'delete') {
          if (data.url === 'specific links') {
            this.specificCounterOverride = data.links_count;
            if (data.links_count >= 0) {
              this.updatedSchema.children.specificScrapedLinksPagination.count = data.links_count;
            }
          } else {
            this.urlOverride = data.links_count > 0 ? data.url : '';
            this.updatedSchema.children.domainCrawledLinksPagination.count = data.links_count ? data.links_count : -1;
            this.updatedSchema.children.url.value = data.links_count > 0 ? data.url : '';
            // if (data.links_count >= 0) {
            //     this.updatedSchema.children.domainCrawledLinksPagination.count = data.links_count;
            // }
          }
        }

        if (data.action === 'scrape') {
          if (data.url === 'specific links') {
            this.specificCounterOverride = data.links_count;
            if (data.links_count >= 0) {
              this.updatedSchema.children.specificScrapedLinksPagination.count = data.links_count;
            }
          } else {
            this.urlOverride = data.url;
            this.updatedSchema.children.url.value = data.url;
            this.updatedSchema.children.domainCrawledLinksPagination.count = data.links_count;
          }
        }

        // remove last scrape date
        if (this.updatedSchema.children.specificScrapedLinksPagination.count <= 0) {
          this.updatedSchema.specificLinksScrapeFinished = null;
        }
        if (this.updatedSchema.children.domainCrawledLinksPagination.count <= 0) {
          this.updatedSchema.domainLinksScrapeFinished = null;
        }

        this.flags = { ...this.flags, ...(data.flags ? data.flags.links : {}) };

        this.resetModalKey++;
        clearTimeout(this.syncElementSchema);
        this.syncElementSchema = setTimeout(() => {
          this.$nextTick(() => {
            this.$eventBus.$emit('sync-data', {
              propName: this.propName,
              clearPropName: this.updatedSchema.children.domainCrawledLinksPagination.count <= 0 ? this.propName : null,
            });
          });
        }, 0);
      }
    },
    resetModal() {
      this.resetModalKey++;
    },
    setData(data) {
      this.urls = data.value;
      this.childrenBody = data.children;
      this.urlOverride = this.urls.entireWeb.value;
    },
    cancelScrape() {
      this.$numberInfoModal.show({
        size: ModalSize.MEDIUM,
        title: 'Cancel link processing',
        message: 'Link processing is still in progress. Are you sure you want to cancel? Any remaining links will not be processed or deleted.',
        confirmButtonText: 'Yes, cancel',
        cancelButtonText: 'No, don\'t cancel',
        variant: ModalVariant.DANGER,
        confirmCallback: () => {
          api('PUT', `organisation/${this.currentOrganisation.id}/assistant/${this.assistantId}/links/cancel-scrape`, null, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                // this.backgroundActionStatusChange({
                //   action: 'webScrape',
                //   assistantId: this.assistantId,
                //   status: StatusTypes.CANCELED,
                // });
                // reset the domainLinksScrapeFinished
                this.updatedSchema.domainLinksScrapeFinished = Math.floor(Date.now() / 1000);
                this.updatedSchema.specificLinksScrapeFinished = Math.floor(Date.now() / 1000);
                this.resetModalKey++;
              }
            });
        },
      });
    },
    reScrape() {
      this.$numberInfoModal.show({
        size: ModalSize.MEDIUM,
        title: 'Start rescrape',
        message: 'Please confirm if you want to rescrape or recrawl the existing website and pages. AI assistant will use existing data until new data is scraped.',
        confirmButtonText: 'Yes, continue',
        confirmCallback: () => {
          console.log('rscp');
          api('PUT', `organisation/${this.currentOrganisation.id}/assistant/${this.assistantId}/links/rescrape`, null, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                // Triggering these right away sometimes messes up the UI so it is better to wait for the pusher.
                // this.addBackgroundActions([{
                //   action: 'webScrape',
                //   assistantId: this.assistantId,
                //   type: 'all',
                //   status: StatusTypes.PENDING,
                // }]);
                // this.addBackgroundActions([{
                //   action: 'webScrape',
                //   assistantId: this.assistantId,
                //   type: 'specific',
                //   status: StatusTypes.PENDING,
                // }]);
                this.$eventBus.$emit('editMessage', {
                  key: 'urlInput',
                  message: 'Rescrape initiated',
                });
              }
            });
        },
      });
    },
    modalClosed() {
      this.dirty = true;
      this.emitData();
    },
    modalInitialized() {
      this.urlsBackup = JSON.parse(JSON.stringify(this.urls));
      // window.eventBus.$emit('reset-changes');
      this.$eventBus.$emit('resetInitialData');
    },
    showUrlsModal() {
      this.urlsBackup = JSON.parse(JSON.stringify(this.urls));
      this.$refs.urlInputModalRef.show(this.urls);
    },
    /**
     * Method will emit value in a standard format as other components used in combination with formSchema which is data/value but it will also include the formSchema value from modal formSchema to enable complete access to the separate modal formSchema.
     */
    emitData(reset = false, force = false) {
      if (!this.$refs.fileObserver || this.preventEmit) return;
      this.hasChanges = !this.$lodash.isEqual(JSON.parse(JSON.stringify(this.urlsBackup)), JSON.parse(JSON.stringify(this.urls)));
      if (!force && !this.hasChanges) return;
      this.$refs.fileObserver.validate({ silent: true })
        .then((valid) => {
          const hasData = this.buttonUrl || this.linkCount;
          this.$emit('input', {
            valid: true, // this.buttonUrl || this.linkCount,
            name: this.schema.returnKey || '',
            data: {
              value: this.urls,
            },
            children: this.childrenBody,
            // postAction: 'webScrape',
            dirty: this.dirty,
            validOneOf: this.schema.validOneOf,
            validOneOfMessage: this.schema.validOneOfMessage,
            hasData, // Indicates that the field has data regardles of it being valid or not. This is used for the form validation where at least one field is required even thou they are not required individually.
          });
          const urlsBackupMinusRescrapeInterval = JSON.parse(JSON.stringify(this.urlsBackup));
          delete urlsBackupMinusRescrapeInterval.rescrape;
          const urlsMinusRescrapeInterval = JSON.parse(JSON.stringify(this.urls));
          delete urlsMinusRescrapeInterval.rescrape;
          const hasChangesExceptRescrapeInterval = !this.$lodash.isEqual(urlsBackupMinusRescrapeInterval, urlsMinusRescrapeInterval);
          if (hasChangesExceptRescrapeInterval) {
            this.forceRescrapeButton = false;
          } else {
            this.forceRescrapeButton = true;
          }
          if (this.initialized && hasChangesExceptRescrapeInterval && hasData && this.schema.editMessage && this.schema.editMessage.default) {
            // Edit message will be triggered only if there are changes on the data and if there are data. Both should be checked because if save is clicked without changes then the empty parameters are set and it is being compared to an empty object resulting with a false positive data available.
            this.$eventBus.$emit('editMessage', {
              key: 'urlInput',
              message: this.schema.editMessage.default,
            });
          } else if (this.initialized && this.hasChangesExceptRescrapeInterval && !hasData && this.schema.editMessage && this.schema.editMessage.empty) {
            this.$eventBus.$emit('editMessage', {
              key: 'urlInput',
              message: this.schema.editMessage.empty,
            });
          } else if (this.initialized && !this.hasChanges) {
            this.$eventBus.$emit('clearEditMessage');
          }
        })
        .finally(() => {
          if (reset && this.$refs.fileObserver) this.$refs.fileObserver.reset();
          if (!this.initialized) {
            this.initialized = true;
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-start-image {
  top: 1px;
  position: relative;
}

.url-button {
  border-radius: 10px !important;
  width: 100%;
  height: 34px;
  padding-top: 8px !important;
  padding-left: 34px !important;
  background-image: url('~@/assets/img/SB-Icons/add-urls.svg');
  text-align: left;
  line-height: normal;

  &.disabled {
    opacity: 1 !important;
  }

  .subtle {
    color: #9eaeb5;
    font-weight: 400;
  }

  &.icon {
    background-size: 14px 14px;
    background-repeat: no-repeat;
    background-position-x: 12px;
    background-position-y: center;

    span {
      // padding-left: 16px;
    }
  }

  &:hover {
    .subtle {
      color: $blackPrimary;
    }
  }

  .filter-btn {
    // margin-top: 4px;
    margin-right: -4px;
    color: $blackUltralight;
    span {
      font-size: 12px;
    }
  }
}

.rescrape-btn:hover {
  * {
    color: $blackPrimary !important;
  }

  ::v-deep .icon.mask {
    background-color: $blackPrimary !important;
  }
}
</style>
